"use client";

import { useState, useEffect, useTransition } from "react";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import StandaloneOTPInput from "@/components/AuthComponents/AuthUI/otp-input";
import { useActionState } from "react";
import {
  confirmRegistrationAction,
  resendRegistrationOtpAction,
} from "@/app/actions/registration";
import { createClient } from "@/utils/supabase/client";
import { logSecurityEvent } from "@/lib/security-log-client";
import { useTranslations } from "next-intl";

interface VerifyEmailProps {
  email: string;
  password: string;
  onNextAction: () => void;
  onBackAction: () => void;
  onErrorAction?: (msg: string) => void;
  firstName: string;
  lastName: string;
  username: string;
}

export default function VerifyEmail({
  email,
  password,
  onNextAction,
  onBackAction,
  onErrorAction,
  firstName,
  lastName,
  username,
}: VerifyEmailProps) {
  const t = useTranslations("auth");
  const tCommon = useTranslations("common");
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isPending, startTransition] = useTransition();

  const [confirmState, confirmFormAction] = useActionState<
    { error?: string; success?: boolean },
    FormData
  >(
    async (_, formData) => {
      const result = await confirmRegistrationAction({
        email: formData.get("email")!.toString(),
        otp: formData.get("otp")!.toString(),
        password: formData.get("password")!.toString(),
        firstName: formData.get("firstName")!.toString(),
        lastName: formData.get("lastName")!.toString(),
        username: formData.get("username")!.toString(),
      });
      return result;
    },
    {}
  );

  const [resendState, resendFormAction] = useActionState<
    { errors?: Record<string, string>; success?: string },
    FormData
  >(
    async (_, formData) => {
      const emailVal = formData.get("email")?.toString() ?? "";
      const result = await resendRegistrationOtpAction(emailVal);
      if (result.errors) return { errors: result.errors };
      return { success: result.success ?? "" };
    },
    {}
  );

  useEffect(() => {
    if (confirmState.error) {
      setErrorMessage(confirmState.error);
      onErrorAction?.(confirmState.error);
      setLoading(false);
    } else if (confirmState.success) {
      setErrorMessage("");
      const supabase = createClient();
      supabase.auth
        .signInWithPassword({ email: email.trim().toLowerCase(), password })
        .then(({ error }) => {
          if (error) {
            setErrorMessage(error.message);
            onErrorAction?.(error.message);
          } else {
            logSecurityEvent({
              event_type: "auth.signup",
              success: true,
              auth_method: "password",
            });
            onNextAction();
          }
        })
        .finally(() => setLoading(false));
    }
  }, [confirmState, email, password, onNextAction, onErrorAction]);

  useEffect(() => {
    if (!resendState.success && !resendState.errors) return;
    if (isResending) {
      if (resendState.errors?.general) {
        setErrorMessage(resendState.errors.general);
      } else if (resendState.success) {
        setInfoMessage(t("newCodeSent"));
        setErrorMessage("");
      }
      setIsResending(false);
    }
    setLoading(false);
  }, [resendState, isResending]);

  const verifyCode = (code: string) => {
    if (code.length !== 6) {
      setErrorMessage(t("enterSixDigitVerification"));
      return;
    }
    if (loading) return;
    setLoading(true);
    setErrorMessage("");
    setInfoMessage("");
    const formData = new FormData();
    formData.append("email", email);
    formData.append("otp", code);
    formData.append("password", password);
    formData.append("firstName", firstName);
    formData.append("lastName", lastName);
    formData.append("username", username);
    startTransition(() => confirmFormAction(formData));
  };

  const handleResend = () => {
    setErrorMessage("");
    setInfoMessage("");
    setLoading(true);
    setIsResending(true);
    const formData = new FormData();
    formData.append("email", email);
    startTransition(() => resendFormAction(formData));
  };

  return (
    <div className="text-center space-y-6 pt-2 relative">
      <button
        onClick={onBackAction}
        className="absolute -top-1 -left-1 min-w-[44px] min-h-[44px] flex items-center justify-center p-2.5 hover:bg-muted/80 rounded-full text-muted-foreground hover:text-foreground transition-colors touch-manipulation"
        aria-label="Go back"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground leading-tight">{t("verifyEmail")}</h2>
      <p className="text-sm text-muted-foreground">
        {t.rich("enterCodeSentTo", {
          email,
          strong: (chunks) => <span className="font-medium text-foreground">{chunks}</span>,
        })}
      </p>

      {errorMessage && (
        <div className="p-3.5 rounded-xl bg-destructive/10 text-destructive text-sm border border-destructive/20">
          {errorMessage}
        </div>
      )}

      {infoMessage && (
        <div className="p-3.5 rounded-xl bg-primary/10 text-foreground text-sm border border-primary/20">
          {infoMessage}
        </div>
      )}

      <div className="flex justify-center my-6">
        <StandaloneOTPInput length={6} onComplete={verifyCode} className="py-4" />
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={handleResend}
          disabled={loading || isPending}
          className="text-sm text-primary hover:underline disabled:opacity-50"
        >
          Didn’t receive a code? Resend
        </button>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        {tCommon.rich("agreeTerms", {
          privacy: (chunks) => <Link href="/privacy" className="underline hover:text-foreground">{chunks}</Link>,
          terms: (chunks) => <Link href="/terms" className="underline hover:text-foreground">{chunks}</Link>,
        })}
      </p>
    </div>
  );
}
